import {
  LONG_RECORDING_IMPORT_THRESHOLD_SECONDS,
  type RecordingStatusChangedPayload,
  type RecordingSummary,
} from "./recording-contracts";
import { isRecordingProcessingStatus, isRecordingWaitExpired } from "./recording-lifecycle";

export type RecordingAvailability = "ready" | "processing" | "retry" | "unavailable";

export const RECORDING_STATUS_LABELS: Record<RecordingSummary["status"], string> = {
  not_expected: "Not recorded",
  capturing: "Recording",
  awaiting_provider: "Preparing",
  archiving: "Archiving",
  composing: "Preparing",
  ready: "Ready",
  partial: "Partial",
  missing: "Missing",
  failed: "Failed",
};

export function getRecordingAvailability(recording: RecordingSummary): RecordingAvailability {
  if (recording.finalAvailable) return "ready";
  if (isRecordingProcessingStatus(recording.status)
    && (recording.processingActive ?? !isRecordingWaitExpired(recording.updatedAt))) {
    return "processing";
  }
  if (recording.canRetry) return "retry";
  return "unavailable";
}

export type RecordingSegment = {
  url: string;
  type: string;
  duration: number;
  timestamp: number;
  stitched?: boolean;
};

export type CallRecordingSource =
  | { kind: "ready"; url: string }
  | { kind: "warm"; legs: { url: string; label: string; segIdx: number }[] }
  | { kind: "processing" }
  | { kind: "failed" }
  | { kind: "unavailable" };

export function getCallRecordingSource(
  callId: string,
  segments: RecordingSegment[] | null | undefined,
  recordingUrl: string | null | undefined,
  transferType: string | null | undefined,
  recording: RecordingSummary | null = null,
  purpose: "playback" | "download" = "playback",
  imported = false,
  processingActive?: boolean,
): CallRecordingSource {
  if (imported) return { kind: "unavailable" };
  const base = purpose === "playback"
    ? `/api/recordings/proxy/${callId}`
    : `/api/call-logs/${callId}/download-recording`;
  if (recording) {
    const availability = getRecordingAvailability(recording);
    if (availability === "ready") return { kind: "ready", url: base };
    if (availability === "processing") return { kind: "processing" };
    return { kind: availability === "retry" ? "failed" : "unavailable" };
  }

  const entries = (Array.isArray(segments) ? segments : [])
    .map((segment, index) => ({ segment, index }));
  const playable = entries.filter(({ segment }) =>
    typeof segment?.url === "string" && segment.url.trim().length > 0 && segment.type !== "stitch_failed");
  const stitched = playable.find(({ segment }) => segment.type === "stitched");
  if (stitched) return { kind: "ready", url: `${base}?segment=${stitched.index}` };
  const raw = playable.filter(({ segment }) => !segment.stitched);

  if (transferType === "warm" && raw.length > 0) {
    return { kind: "warm", legs: raw.map(({ segment, index }) => ({
      url: `${base}?segment=${index}`,
      label: segment.type === "conference_talk" ? "Transfer Leg"
        : segment.type === "conference_queue" ? "Queue Hold" : "Call Recording",
      segIdx: index,
    })) };
  }
  if (entries.some(({ segment }) => segment?.type === "stitch_failed")) return { kind: "failed" };
  if (raw.length > 1) {
    const latestSegmentTime = Math.max(0, ...raw.map(({ segment }) =>
      Number.isFinite(segment.timestamp) ? segment.timestamp : 0));
    const processing = processingActive ?? (latestSegmentTime > 0 && !isRecordingWaitExpired(latestSegmentTime));
    return { kind: processing ? "processing" : "unavailable" };
  }
  if (raw.length === 1) return { kind: "ready", url: `${base}?segment=${raw[0].index}` };
  if (recordingUrl?.trim()) return { kind: "ready", url: base };
  return { kind: "unavailable" };
}

export type CallRecordingPresentation = {
  status: "available" | "preparing" | "unavailable" | "not_recorded";
  label: string;
  description: string;
};

export function getCallRecordingPresentation(
  source: CallRecordingSource,
  recording: RecordingSummary | null,
  imported = false,
): CallRecordingPresentation {
  if (imported) return {
    status: "unavailable", label: "Unavailable", description: "Recording playback is unavailable for imported calls.",
  };
  const available = source.kind === "ready" || source.kind === "warm";
  const status = available ? "available" : source.kind === "processing" ? "preparing"
    : recording?.status === "not_expected" ? "not_recorded" : "unavailable";
  const label = { available: "Available", preparing: "Preparing", unavailable: "Unavailable", not_recorded: "Not recorded" }[status];
  const details = [available ? "Recording is available to listen to."
    : status === "preparing" ? "Recording is being prepared."
    : status === "not_recorded" ? "Recording was not enabled for this call."
    : "No recording is available for this call."];
  if (recording?.status === "partial") details.push("The recording is partial; some audio may be missing.");
  if (recording?.paused) details.push("Recording is paused.");
  if (recording?.failureCode === "recording_wait_expired") details.push("The recording wait period ended without an available final recording.");
  if (recording?.failureCode === "call_completion_missing") details.push("Recorded audio is retained, but processing is blocked because the call has no completion time.");
  if (recording?.failureCode === "call_not_answered") details.push("The call ended without an answered conversation or voicemail, and no recording has been received.");
  if (recording?.status === "failed" || source.kind === "failed") details.push("A recording processing stage failed.");
  if (recording?.canRetry || source.kind === "failed") details.push("You can retry the missing or failed recording stage from Actions.");
  if (source.kind === "warm") details.push("The recording contains separate transfer parts.");
  return { status, label, description: details.join(" ") };
}

export function isLongRecordingImportEligible(params: {
  durationSeconds: number | null;
  completed: boolean;
  hasProviderCallUuid: boolean;
  recording: RecordingSummary | null;
}): boolean {
  return params.durationSeconds !== null
    && Number.isFinite(params.durationSeconds)
    && params.durationSeconds > LONG_RECORDING_IMPORT_THRESHOLD_SECONDS
    && params.completed
    && params.hasProviderCallUuid
    && params.recording?.status === "ready"
    && params.recording.finalAvailable;
}

export function shouldRefreshRecordingStatus(
  visibleCallLogIds: readonly string[],
  payload: RecordingStatusChangedPayload,
): boolean {
  return visibleCallLogIds.includes(payload.callLogId);
}

export type RecordingCaptureCommandResult = {
  success: boolean;
  paused: boolean;
  message: string | null;
};

export function parseRecordingCaptureCommandResult(payload: unknown): RecordingCaptureCommandResult | null {
  if (typeof payload !== "object" || payload === null || Array.isArray(payload)) return null;
  const record = payload as Record<string, unknown>;
  if (typeof record.success !== "boolean" || typeof record.paused !== "boolean") return null;
  return {
    success: record.success,
    paused: record.paused,
    message: typeof record.message === "string" ? record.message : null,
  };
}
